import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIBadge from 'UIComponents/badge/UIBadge';
import styled, { css } from 'styled-components';
import { LORAX_DARK, LORAX_LIGHT } from 'HubStyleTokens/colors';
import I18n from 'I18n';
import { localeArray } from 'developer-docs-shared-components/context/LocaleContext';
import memoizeOne from 'react-utils/memoizeOne';
import { docsCtxKeyToPath } from './mdx';
import { getPrimaryNavItems } from '../config/nav/primaryNavConfig';
import { urlKeys } from 'developer-docs-source/urlKeys';

/**
 * Type used to map user-facing absolute URL strings to a boolean for each
 * locale. If a locale key (e.g. `es-es`) value is `true`, a localized version
 * of that route exists for that locale.
 *
 * @example
 * ```@tsx
 * {
 *   "/getting-started/account-types": {
 *       "en-us": true,
 *       "de-de": true,
 *       "es-es": false,
 *       "fr-fr": false,
 *       "ja-jp": true,
 *       "pt-br": false
 *   },
 *   // ...
 * }
 * ```
 */
import { jsx as _jsx } from "react/jsx-runtime";
export const localizeTextTransform = css(["html:not([lang*='en']) &{text-transform:none;}"]);

/**
 * Exported to allow overriding styling in certain contexts.
 */
export const LocalizedStatusBadge = styled(UIBadge).withConfig({
  displayName: "localization__LocalizedStatusBadge",
  componentId: "sc-1bslnpy-0"
})(["", " white-space:nowrap;"], localizeTextTransform);
const EnglishBadge = styled(LocalizedStatusBadge).withConfig({
  displayName: "localization__EnglishBadge",
  componentId: "sc-1bslnpy-1"
})(["background-color:", ";color:", ";"], LORAX_LIGHT, LORAX_DARK);

/**
 * Returns a localized node of a `UIBadge`, if applicable, for `BETA`,
 * `NEW`, and `EN` badges. In the primary nav submenu, badges should render as
 * parenthetical suffixes (e.g. `BETA_SUFFIX`, which renders as `(Beta)` in
 * `en-us`).
 */
export const getLocalizedBadge = badge => {
  const sharedBadgeProps = {
    elements: {
      Badge: LocalizedStatusBadge
    }
  };
  if (badge === 'NEW') {
    return /*#__PURE__*/_jsx(FormattedJSXMessage, Object.assign({}, sharedBadgeProps, {
      message: "developer-docs-ui.badges.new_jsx",
      options: {
        use: 'oz'
      }
    }));
  } else if (badge === 'BETA') {
    return /*#__PURE__*/_jsx(FormattedJSXMessage, Object.assign({}, sharedBadgeProps, {
      message: "developer-docs-ui.badges.beta_jsx",
      options: {
        use: 'beta'
      }
    }));
  } else if (badge === 'BETA_SUFFIX') {
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "developer-docs-ui.badges.betaSuffix",
      useGap: true
    });
  } else if (badge === 'EN') {
    return /*#__PURE__*/_jsx(EnglishBadge, {
      "aria-label": I18n.text('developer-docs-ui.accessibility.englishDocSuffix'),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "developer-docs-mdx-components.localization.englishAbbreviation"
      })
    });
  }
  return null;
};

/**
 * @internal
 * Set a route mapping to this variable when a route is fully translated.
 */
const translatedRouteObj = {
  'en-us': true,
  'pt-br': true,
  'fr-fr': true,
  'es-es': true,
  'de-de': true,
  'ja-jp': true
};

/**
 * @internal
 * Set a route mapping to this variable when a route is not fully translated.
 */
const untranslatedRouteObj = {
  'en-us': true,
  'pt-br': false,
  'fr-fr': false,
  'es-es': false,
  'de-de': false,
  'ja-jp': false
};

/**
 * @internal
 * API Reference paths where localization is handled via SHADE.
 */
const shadeTranslations = urlKeys['en-us']['reference/api']['spec'].reduce((acc, key) => {
  acc[`/reference/api/${key}`] = translatedRouteObj;
  return acc;
}, {});

/**
 * A mapping of user-facing absolute URLs to a boolean for each
 * locale. If a locale key (e.g. `es-es`) value is `true`, a localized version
 * of that route exists for that locale.
 *
 * @example
 * ```tsx
 * {
 *   "/getting-started/account-types": {
 *       "en-us": true,
 *       "de-de": true,
 *       "es-es": false,
 *       "fr-fr": false,
 *       "ja-jp": true,
 *       "pt-br": false
 *   },
 *   // ...
 * }
 * ```
 *
 * @note
 * SHADE/endpoint data is localized separately from MDX files. A localized URL
 * will always exist for pages reliant on those services.
 */
export const getLocalizedRouteMap = memoizeOne(() => {
  const baseTranslations = shadeTranslations;
  localeArray.forEach(locale => {
    Object.keys(urlKeys[locale]).forEach(section => {
      (section === 'reference/api' ?
      // all of the values at [reference/api].spec are localized, so just need to check the mdx files
      urlKeys[locale]['reference/api'].mdx : urlKeys[locale][section]).forEach(path => {
        const absolutePath = docsCtxKeyToPath(`./${section}/${path}`);
        if (!baseTranslations[absolutePath]) {
          // mark as untranslated by default
          baseTranslations[absolutePath] = Object.assign({}, untranslatedRouteObj);
        }
        baseTranslations[absolutePath][locale] = true;
      });
    });
  });
  const primaryNavConfigs = getPrimaryNavItems();
  primaryNavConfigs.forEach(({
    getSecondaryNavConfigs
  }) => {
    const secondaryNavConfigs = getSecondaryNavConfigs();
    secondaryNavConfigs.forEach(secondaryNavConfig => {
      const {
        commonPath
      } = secondaryNavConfig();
      baseTranslations[commonPath] = translatedRouteObj;
    });
  });
  return baseTranslations;
});

/**
 * Checks if an absolute URL is localized in the current locale. All `en-us`
 * routes are localized.
 */
export const isUrlLocalized = memoizeOne((absoluteUrl, currentLocale) => {
  const translations = getLocalizedRouteMap();
  const urlMap = translations[absoluteUrl];
  return currentLocale === 'en-us' || !!urlMap && !!urlMap[currentLocale];
});